import { useEffect, useState } from 'react'
import {
  Combobox,
  ETypeOfNotification,
  InputDate,
  InputTextCustom,
  setNotification,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useDispatch } from 'react-redux'
import { useCalendarHolidayService } from '../../servicesHooks/CalendarHoliday/useCalendarHolidayService'
import {
  CalendarHolidayItem,
  CalendarHolidayLoadType,
  CalendarHolidayOfficialItem,
} from '../../interface/CalendarHolidayTypes'

interface CalendarHolidayCrudPanelProps {
  holidayId: number
  initialHoliday?: CalendarHolidayItem | null
  onClose: () => void
  onSaved?: () => void
}

interface FormErrors {
  date?: string
  reason?: string
  description?: string
  official?: string
}

interface LoadTypeOption {
  id: CalendarHolidayLoadType
  name: string
}

const loadTypeOptions: LoadTypeOption[] = [
  { id: 'manual', name: 'Manual' },
  { id: 'official', name: 'Calendario oficial' },
]

function formatDate(dateText: string): string {
  if (!dateText) return ''
  const date = new Date(`${dateText}T00:00:00`)
  if (Number.isNaN(date.getTime())) return dateText
  return date.toLocaleDateString('es-AR')
}

function formatDateForBackend(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseIsoToDate(dateText: string): Date | null {
  if (!dateText) return null
  const parsed = new Date(`${dateText}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function officialKey(item: CalendarHolidayOfficialItem): string {
  return `${item.date}|${item.reason}|${item.description}`
}

function sortByDate(a: CalendarHolidayOfficialItem, b: CalendarHolidayOfficialItem): number {
  return a.date.localeCompare(b.date)
}

export default function CalendarHolidayCrudPanel({
  holidayId,
  initialHoliday,
  onClose,
  onSaved,
}: Readonly<CalendarHolidayCrudPanelProps>) {
  const dispatch = useDispatch()
  const {
    createManualHoliday,
    getOfficialHolidays,
    importOfficialHolidays,
    updateManualHoliday,
  } = useCalendarHolidayService()
  const [loadTypeOption, setLoadTypeOption] = useState<LoadTypeOption>(loadTypeOptions[0])
  const [dateValue, setDateValue] = useState<Date | null>(parseIsoToDate(initialHoliday?.date ?? ''))
  const [reason, setReason] = useState(initialHoliday?.reason ?? '')
  const [description, setDescription] = useState(initialHoliday?.description ?? '')
  const [officialItems, setOfficialItems] = useState<CalendarHolidayOfficialItem[]>([])
  const [selectedOfficialItems, setSelectedOfficialItems] = useState<CalendarHolidayOfficialItem[]>([])
  const [loadingOfficial, setLoadingOfficial] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const isEditMode = holidayId > 0
  const loadType: CalendarHolidayLoadType = isEditMode ? 'manual' : loadTypeOption.id

  useEffect(() => {
    if (loadType === 'official') {
      void loadOfficialCalendar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadType])

  async function loadOfficialCalendar() {
    setLoadingOfficial(true)
    setErrors((prev) => ({ ...prev, official: undefined }))

    try {
      const currentYear = new Date().getFullYear()
      const [currentYearItems, nextYearItems] = await Promise.all([
        getOfficialHolidays(currentYear),
        getOfficialHolidays(currentYear + 1),
      ])

      const allItems = [...currentYearItems, ...nextYearItems]
      const cutoff = `${currentYear}-01-01`
      const dedupMap = new Map<string, CalendarHolidayOfficialItem>()

      for (const item of allItems) {
        if (item.date < cutoff) continue
        dedupMap.set(officialKey(item), item)
      }

      const mergedItems = Array.from(dedupMap.values()).sort(sortByDate)

      setOfficialItems(mergedItems)
      setSelectedOfficialItems((prev) => {
        const allowedKeys = new Set(mergedItems.map((item) => officialKey(item)))
        return prev.filter((item) => allowedKeys.has(officialKey(item)))
      })
    } catch (error) {
      console.error(error)
      dispatch(setNotification({
        title: 'Error',
        desc: 'No se pudieron cargar los feriados oficiales.',
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    } finally {
      setLoadingOfficial(false)
    }
  }

  function toggleOfficialItem(item: CalendarHolidayOfficialItem) {
    const itemKey = officialKey(item)
    setSelectedOfficialItems((prev) => {
      const exists = prev.some((candidate) => officialKey(candidate) === itemKey)
      if (exists) {
        return prev.filter((candidate) => officialKey(candidate) !== itemKey)
      }
      return [...prev, item]
    })
    setErrors((prev) => ({ ...prev, official: undefined }))
  }

  function isOfficialItemSelected(item: CalendarHolidayOfficialItem) {
    const itemKey = officialKey(item)
    return selectedOfficialItems.some((candidate) => officialKey(candidate) === itemKey)
  }

  function toggleAllOfficialItems() {
    const allSelected = officialItems.length > 0
      && officialItems.every((item) => isOfficialItemSelected(item))

    if (allSelected) {
      setSelectedOfficialItems([])
      return
    }

    setSelectedOfficialItems([...officialItems])
    setErrors((prev) => ({ ...prev, official: undefined }))
  }

  function validateManual(): FormErrors {
    const nextErrors: FormErrors = {}
    if (!dateValue) {
      nextErrors.date = 'La fecha es obligatoria'
    }
    if (reason.trim() === '') {
      nextErrors.reason = 'El motivo es obligatorio'
    }
    if (description.trim() === '') {
      nextErrors.description = 'La descripción es obligatoria'
    }
    return nextErrors
  }

  function validateOfficial(): FormErrors {
    const nextErrors: FormErrors = {}
    if (selectedOfficialItems.length === 0) {
      nextErrors.official = 'Seleccione al menos un feriado para importar'
    }
    return nextErrors
  }

  function resetManualForm() {
    setDateValue(null)
    setReason('')
    setDescription('')
  }

  async function save(closeAfterSave = true) {
    const validationErrors = loadType === 'manual' ? validateManual() : validateOfficial()
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length > 0) {
      dispatch(setNotification({
        title: 'Validación',
        desc: Object.values(validationErrors)[0] ?? 'Complete los datos obligatorios',
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-exclamation-triangle',
      }))
      return
    }

    setSaving(true)

    try {
      if (loadType === 'manual') {
        const payload = {
          date: formatDateForBackend(dateValue as Date),
          reason: reason.trim(),
          description: description.trim(),
        }

        if (isEditMode) {
          await updateManualHoliday(holidayId, payload)
          dispatch(setNotification({
            title: 'Feriado actualizado',
            desc: 'Los cambios se guardaron correctamente.',
            typeOfNotification: ETypeOfNotification.INFO,
            icon: 'pi pi-check',
          }))
        } else {
          await createManualHoliday(payload)
          dispatch(setNotification({
            title: 'Feriado agregado',
            desc: 'El feriado se agrego correctamente.',
            typeOfNotification: ETypeOfNotification.INFO,
            icon: 'pi pi-check',
          }))
        }
      } else {
        const importResult = await importOfficialHolidays(selectedOfficialItems)
        const infoMessage = importResult.rejected > 0
          ? `Se importaron ${importResult.imported} feriados. ${importResult.rejected} no pudieron agregarse.`
          : `Se importaron ${importResult.imported} feriados correctamente.`

        dispatch(setNotification({
          title: 'Importacion finalizada',
          desc: infoMessage,
          typeOfNotification: ETypeOfNotification.INFO,
          icon: 'pi pi-check',
        }))
      }

      onSaved?.()

      if (closeAfterSave) {
        onClose()
      } else {
        if (loadType === 'manual') {
          resetManualForm()
        } else {
          setSelectedOfficialItems([])
          void loadOfficialCalendar()
        }
        setErrors({})
      }
    } catch (error: unknown) {
      const response = (typeof error === 'object' && error !== null && 'response' in error)
        ? (error as { response?: { status?: number } }).response
        : undefined
      const status = typeof response?.status === 'number' ? response.status : null

      if (status === 409) {
        dispatch(setNotification({
          title: 'No se pudo guardar',
          desc: 'Ya existe un feriado en esa fecha para este sitio.',
          typeOfNotification: ETypeOfNotification.WARNING,
          icon: 'pi pi-exclamation-triangle',
        }))
      } else {
        dispatch(setNotification({
          title: 'No se pudieron guardar los cambios del feriado.',
          typeOfNotification: ETypeOfNotification.ERROR,
          icon: 'pi pi-times',
        }))
      }
    } finally {
      setSaving(false)
    }
  }

  const allOfficialSelected = officialItems.length > 0
    && officialItems.every((item) => isOfficialItemSelected(item))
  const someOfficialSelected = selectedOfficialItems.length > 0 && !allOfficialSelected

  return (
    <div className='viewContainer calendar-holiday-view'>
      <div className='calendar-holiday-back-action'>
        <Button className='btnCancel' label='<< Volver' onClick={onClose} />
      </div>

      <h1 className='calendar-holiday-title mb-2' >Agregar Feriados</h1>
      <div className='calendar-holiday-divider' />

      {!isEditMode && (
        <div className='calendar-holiday-loadtype-row'>
          <div className='calendar-holiday-loadtype-field'>
            <label className='calendar-holiday-label'>Tipo de carga</label>
            <Combobox
              className='w-full'
              getItems={() => Promise.resolve(loadTypeOptions)}
              value={loadTypeOption}
              setValue={(value: LoadTypeOption | null) => {
                setLoadTypeOption(value ?? loadTypeOptions[0])
                setErrors({})
              }}
              optionLabel='name'
            />
          </div>
        </div>
      )}

      {loadType === 'manual' && (
        <div className='calendar-holiday-manual-grid'>
            <InputDate
            className='w-full'
            label='Fecha *'
            value={dateValue}
            onChange={(e) => {
              setDateValue(e?.target?.value ?? null)
              setErrors((prev) => ({ ...prev, date: undefined }))
            }}
            error={Boolean(errors.date)}
            caption={errors.date}
          />
            <InputTextCustom
            className='w-full'
            label='Motivo *'
            value={reason}
            onChange={(e) => {
              setReason(e?.target?.value ?? '')
              setErrors((prev) => ({ ...prev, reason: undefined }))
            }}
            error={Boolean(errors.reason)}
            caption={errors.reason}
          />
            <InputTextCustom
            className='w-full'
            label='Descripción *'
            value={description}
            onChange={(e) => {
              setDescription(e?.target?.value ?? '')
              setErrors((prev) => ({ ...prev, description: undefined }))
            }}
            error={Boolean(errors.description)}
            caption={errors.description}
          />
           
        </div>
      )}

      {loadType === 'official' && (
        <div className='calendar-holiday-official-section'>
          <h3 className='calendar-holiday-official-title'>Calendario Oficial de feriados Nacionales publicados</h3>

          <table className='calendar-holiday-official-html-table'>
            <thead>
              <tr>
                <th>
                  <input
                    type='checkbox'
                    checked={allOfficialSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = someOfficialSelected
                      }
                    }}
                    onChange={toggleAllOfficialItems}
                    disabled={loadingOfficial || officialItems.length === 0}
                  />
                </th>
                <th>Fecha</th>
                <th>Motivo</th>
                <th>Descripción</th>
              </tr>
            </thead>
            <tbody>
              {loadingOfficial && (
                <tr>
                  <td colSpan={4}>Cargando feriados oficiales...</td>
                </tr>
              )}

              {!loadingOfficial && officialItems.map((item) => {
                const rowKey = officialKey(item)
                const checked = isOfficialItemSelected(item)
                return (
                  <tr key={rowKey}>
                    <td>
                      <input
                        type='checkbox'
                        checked={checked}
                        onChange={() => toggleOfficialItem(item)}
                      />
                    </td>
                    <td>{formatDate(item.date)}</td>
                    <td>{item.reason}</td>
                    <td>{item.description}</td>
                  </tr>
                )
              })}

              {!loadingOfficial && officialItems.length === 0 && (
                <tr>
                  <td colSpan={4}>No hay feriados oficiales disponibles.</td>
                </tr>
              )}
            </tbody>
          </table>

          {errors.official && <small className='p-error block mt-2'>{errors.official}</small>}

          {selectedOfficialItems.length > 0 && (
            <small className='calendar-holiday-selected-count'>
              {selectedOfficialItems.length} feriado(s) seleccionado(s)
            </small>
          )}
        </div>
      )}

      <div className='calendar-holiday-crud-actions'>
        <Button
          className='buttonMain2 calendar-holiday-action-btn calendar-holiday-cancel-btn'
          label='Cancelar'
          onClick={onClose}
          disabled={saving}
        />
        {!isEditMode && (
          <Button
            className='calendar-holiday-save-and-add-btn calendar-holiday-action-btn'
            label={saving ? 'Guardando...' : 'Guardar y agregar otro'}
            onClick={() => { void save(false) }}
            disabled={saving}
          />
        )}
        <Button
          className='buttonMain3 calendar-holiday-action-btn calendar-holiday-primary-btn'
          label={saving ? 'Guardando...' : isEditMode ? 'Guardar cambios' : 'Confirmar'}
          onClick={() => { void save() }}
          disabled={saving}
        />
      </div>
    </div>
  )
}
